import {
  ApplicationConfig,
  inject,
  isDevMode,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideRouter, withPreloading, PreloadAllModules } from '@angular/router';
import { provideTransloco, provideTranslocoLoader, TranslocoService } from '@jsverse/transloco';
import { TranslocoHttpLoaderService } from './core/services/transloco-http-loader.service';

import { routes } from './app.routes';
import { provideApi } from './core/api';
import { environment } from '../environment/environment';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { idempotencyInterceptor } from './core/interceptors/idempotency.interceptor';
import { AuthSessionService } from './core/services/auth-session.service';
import { LanguageService, SA_LANGUAGES } from './core/services/language.service';
import { firstValueFrom } from 'rxjs';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),

    provideHttpClient(withFetch(), withInterceptors([idempotencyInterceptor, authInterceptor])),

    provideApi({
      basePath: environment.apiUrl,
      // DO NOT TOUCH
      // Ensures that cookies (including refresh tokens) are sent with each request
      withCredentials: true,
    }),

    provideRouter(routes, withPreloading(PreloadAllModules)),

    // ─── Transloco i18n (11 South African official languages) ───────────────
    provideTransloco({
      config: {
        availableLangs: SA_LANGUAGES.map(l => l.code),
        defaultLang: 'en',
        fallbackLang: 'en',
        // Seamlessly re-render all transloco directives when the language changes
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        missingHandler: {
          // In dev mode surface missing keys; in prod silently fall back to the key itself
          useFallbackTranslation: true,
          logMissingKey: isDevMode(),
        },
      },
    }),
    // Translation files served from /assets/i18n/<lang>.json
    provideTranslocoLoader(TranslocoHttpLoaderService),

    // ─── App initialisers ────────────────────────────────────────────────────
    provideAppInitializer(async () => {
      // 1. Apply the user's persisted language preference and wait for the
      //    translation file to load so components never render with missing keys.
      const langService = inject(LanguageService);
      const transloco = inject(TranslocoService);
      langService.init();
      await firstValueFrom(transloco.load(transloco.getActiveLang()));
    }),
    provideAppInitializer(async () => {
      // 2. Refresh the auth session (fail silently — guard handles redirect)
      const auth = inject(AuthSessionService);
      try {
        await firstValueFrom(auth.refresh());
        // Roles survive in localStorage across reloads; re-fetch only when
        // localStorage was cleared while the refresh-token cookie was still valid.
        if (auth.roles().length === 0) {
          await firstValueFrom(auth.fetchAndStoreRoles()).catch(() => {});
        }
      } catch {
        // Fail silently, user will be redirected to login page
        // by the auth guard when trying to access protected routes
      }
    }),
  ],
};
